// Sidebar Component v1.0.0 - Controls, filters, and mask authorization
import { useState, useCallback } from 'react'
import { useViewerStore } from '../stores/viewerStore'
import type { CameraChannel, ClipEventType } from '../types'
import { CAMERA_LABELS } from '../types'
import { requestMaskAuthorization, verifyMaskAuthorization } from '../api/client'

export default function Sidebar() {
  const {
    // Channel selection
    selectedChannels,
    toggleChannel,
    selectAllChannels,
    layout,
    setLayout,
    primaryChannel,
    // Filters
    eventTypeFilter,
    toggleEventTypeFilter,
    threatLevelFilter,
    toggleThreatLevelFilter,
    showOnlyWithDetections,
    setShowOnlyWithDetections,
    // Playback
    playbackSpeed,
    setPlaybackSpeed,
    // Display options
    showDetectionOverlay,
    toggleDetectionOverlay,
    showTimestamps,
    toggleTimestamps,
    // Child mask
    maskChildDetections,
    setMaskChildDetections,
    activeMaskAuthorizations,
    addMaskAuthorization,
    removeMaskAuthorization,
    // Current state
    currentClips,
  } = useViewerStore()

  const [showAuthDialog, setShowAuthDialog] = useState(false)
  const [authCode, setAuthCode] = useState('')
  const [authReason, setAuthReason] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)

  // Get current clip with child content
  const currentClipWithChildren = Array.from(currentClips.values()).find(
    (clip) => clip?.analysis?.hasChildDetection
  )

  // Handle mask authorization request
  const handleRequestAuth = useCallback(async () => {
    if (!currentClipWithChildren || !authReason.trim()) {
      setAuthError('Please provide a reason for viewing protected content')
      return
    }

    setIsVerifying(true)
    setAuthError(null)

    try {
      const response = await requestMaskAuthorization({
        clipId: currentClipWithChildren.id,
        reason: authReason,
        requestedBy: 'viewer-user', // Would come from auth system
      })

      if (response.requiresApproval) {
        setAuthError('Authorization request submitted. Awaiting approval.')
      } else {
        addMaskAuthorization(response.authorization)
        setShowAuthDialog(false)
        setAuthReason('')
      }
    } catch (e) {
      setAuthError('Failed to request authorization')
    } finally {
      setIsVerifying(false)
    }
  }, [currentClipWithChildren, authReason, addMaskAuthorization])

  // Handle auth code verification
  const handleVerifyCode = useCallback(async () => {
    if (!authCode.trim()) {
      setAuthError('Please enter an authorization code')
      return
    }

    setIsVerifying(true)
    setAuthError(null)

    try {
      const auth = await verifyMaskAuthorization(authCode)
      if (auth && auth.status === 'approved') {
        addMaskAuthorization(auth)
        setShowAuthDialog(false)
        setAuthCode('')
      } else {
        setAuthError('Invalid or expired authorization code')
      }
    } catch (e) {
      setAuthError('Failed to verify authorization code')
    } finally {
      setIsVerifying(false)
    }
  }, [authCode, addMaskAuthorization])

  const allChannels: CameraChannel[] = ['front', 'back', 'left_repeater', 'right_repeater']
  const allEventTypes: ClipEventType[] = ['SentryMode', 'SavedClip', 'RecentClips', 'Honk', 'UserInteraction']
  const threatLevels = ['none', 'low', 'medium', 'high', 'critical'] as const

  return (
    <aside className="w-72 bg-viewer-surface border-l border-viewer-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-viewer-border">
        <h2 className="text-lg font-semibold">Timeline Viewer</h2>
        <p className="text-xs text-viewer-text-secondary mt-1">Tesla Dashcam Playback</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Channel Selection */}
        <section>
          <h3 className="text-sm font-medium text-viewer-text-secondary mb-2">CAMERAS</h3>
          <div className="space-y-1">
            {allChannels.map((channel) => (
              <label
                key={channel}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-viewer-surface-hover cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedChannels.includes(channel)}
                  onChange={() => toggleChannel(channel)}
                  className="rounded border-viewer-border bg-viewer-bg text-viewer-accent focus:ring-viewer-accent"
                />
                <span className="text-sm">{CAMERA_LABELS[channel]}</span>
                {channel === primaryChannel && (
                  <span className="ml-auto text-xs text-viewer-accent">Primary</span>
                )}
              </label>
            ))}
          </div>
          <button
            onClick={selectAllChannels}
            className="mt-2 w-full px-3 py-1.5 text-xs rounded bg-viewer-surface-hover hover:bg-viewer-border"
          >
            Select All
          </button>
        </section>

        {/* Layout */}
        <section>
          <h3 className="text-sm font-medium text-viewer-text-secondary mb-2">LAYOUT</h3>
          <div className="grid grid-cols-3 gap-1">
            {(['single', 'grid', 'pip'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLayout(l)}
                className={`px-3 py-2 text-xs rounded capitalize ${
                  layout === l ? 'bg-viewer-accent text-white' : 'bg-viewer-surface-hover hover:bg-viewer-border'
                }`}
              >
                {l === 'pip' ? 'PIP' : l}
              </button>
            ))}
          </div>
        </section>

        {/* Playback Speed */}
        <section>
          <h3 className="text-sm font-medium text-viewer-text-secondary mb-2">SPEED</h3>
          <div className="grid grid-cols-5 gap-1">
            {[0.5, 1, 2, 4, 8].map((speed) => (
              <button
                key={speed}
                onClick={() => setPlaybackSpeed(speed)}
                className={`px-2 py-1.5 text-xs rounded ${
                  playbackSpeed === speed ? 'bg-viewer-accent text-white' : 'bg-viewer-surface-hover hover:bg-viewer-border'
                }`}
              >
                {speed}x
              </button>
            ))}
          </div>
        </section>

        {/* Event Type Filter */}
        <section>
          <h3 className="text-sm font-medium text-viewer-text-secondary mb-2">EVENT TYPES</h3>
          <div className="space-y-1">
            {allEventTypes.map((type) => (
              <label
                key={type}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-viewer-surface-hover cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={eventTypeFilter.includes(type)}
                  onChange={() => toggleEventTypeFilter(type)}
                  className="rounded border-viewer-border bg-viewer-bg text-viewer-accent focus:ring-viewer-accent"
                />
                <span className="text-sm">{type.replace(/([A-Z])/g, ' $1').trim()}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Threat Level Filter */}
        <section>
          <h3 className="text-sm font-medium text-viewer-text-secondary mb-2">THREAT LEVEL</h3>
          <div className="flex flex-wrap gap-1">
            {threatLevels.map((level) => {
              const colors: Record<typeof level, string> = {
                none: 'bg-viewer-text-muted',
                low: 'bg-viewer-success',
                medium: 'bg-viewer-warning',
                high: 'bg-viewer-danger',
                critical: 'bg-red-600',
              }
              return (
                <button
                  key={level}
                  onClick={() => toggleThreatLevelFilter(level)}
                  className={`px-2 py-1 text-xs rounded capitalize flex items-center gap-1 ${
                    threatLevelFilter.includes(level)
                      ? 'ring-2 ring-viewer-accent'
                      : 'opacity-50'
                  } ${colors[level]}`}
                >
                  {level}
                </button>
              )
            })}
          </div>
        </section>

        {/* Display Options */}
        <section>
          <h3 className="text-sm font-medium text-viewer-text-secondary mb-2">DISPLAY</h3>
          <div className="space-y-1">
            <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-viewer-surface-hover cursor-pointer">
              <input
                type="checkbox"
                checked={showDetectionOverlay}
                onChange={toggleDetectionOverlay}
                className="rounded border-viewer-border bg-viewer-bg text-viewer-accent focus:ring-viewer-accent"
              />
              <span className="text-sm">Detection Boxes</span>
            </label>
            <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-viewer-surface-hover cursor-pointer">
              <input
                type="checkbox"
                checked={showTimestamps}
                onChange={toggleTimestamps}
                className="rounded border-viewer-border bg-viewer-bg text-viewer-accent focus:ring-viewer-accent"
              />
              <span className="text-sm">Timestamps</span>
            </label>
            <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-viewer-surface-hover cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyWithDetections}
                onChange={(e) => setShowOnlyWithDetections(e.target.checked)}
                className="rounded border-viewer-border bg-viewer-bg text-viewer-accent focus:ring-viewer-accent"
              />
              <span className="text-sm">Only with Detections</span>
            </label>
          </div>
        </section>

        {/* Child Protection */}
        <section className="p-3 rounded-lg bg-detection-child/10 border border-detection-child/30">
          <h3 className="text-sm font-medium text-detection-child mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            CHILD PROTECTION
          </h3>
          <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-detection-child/10 cursor-pointer">
            <input
              type="checkbox"
              checked={maskChildDetections}
              onChange={(e) => setMaskChildDetections(e.target.checked)}
              className="rounded border-detection-child bg-viewer-bg text-detection-child focus:ring-detection-child"
            />
            <span className="text-sm">Mask Child Detections</span>
          </label>

          {/* Active authorizations */}
          {activeMaskAuthorizations.length > 0 && (
            <div className="mt-2 space-y-1">
              <span className="text-xs text-viewer-text-secondary">Active Authorizations:</span>
              {activeMaskAuthorizations.map((auth) => (
                <div
                  key={auth.id}
                  className="flex items-center justify-between px-2 py-1 bg-viewer-bg rounded text-xs"
                >
                  <span className="truncate">{auth.reason}</span>
                  <button
                    onClick={() => removeMaskAuthorization(auth.id)}
                    className="text-viewer-danger hover:text-red-400"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Request authorization button */}
          {currentClipWithChildren && maskChildDetections && (
            <button
              onClick={() => setShowAuthDialog(true)}
              className="mt-2 w-full px-3 py-2 text-sm rounded bg-detection-child text-white hover:bg-detection-child/80"
            >
              Request Authorization
            </button>
          )}
        </section>
      </div>

      {/* Authorization Dialog */}
      {showAuthDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-viewer-surface rounded-lg p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-detection-child" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              Child Content Authorization
            </h3>

            <p className="text-sm text-viewer-text-secondary mb-4">
              This footage contains detected children. To view unmasked content, you must provide
              a valid reason and obtain authorization.
            </p>

            {/* Enter existing code */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Authorization Code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={authCode}
                  onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  className="flex-1 px-3 py-2 bg-viewer-bg border border-viewer-border rounded text-sm font-mono"
                />
                <button
                  onClick={handleVerifyCode}
                  disabled={isVerifying || !authCode}
                  className="px-4 py-2 bg-viewer-accent text-white rounded text-sm disabled:opacity-50"
                >
                  Verify
                </button>
              </div>
            </div>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-viewer-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-viewer-surface text-viewer-text-secondary">or request new</span>
              </div>
            </div>

            {/* Request new authorization */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Reason for Access</label>
              <textarea
                value={authReason}
                onChange={(e) => setAuthReason(e.target.value)}
                placeholder="e.g., Vehicle incident investigation, insurance claim #12345"
                rows={3}
                className="w-full px-3 py-2 bg-viewer-bg border border-viewer-border rounded text-sm resize-none"
              />
            </div>

            {authError && (
              <div className="mb-4 px-3 py-2 bg-viewer-danger/20 text-viewer-danger rounded text-sm">
                {authError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowAuthDialog(false)
                  setAuthCode('')
                  setAuthReason('')
                  setAuthError(null)
                }}
                className="flex-1 px-4 py-2 bg-viewer-surface-hover text-viewer-text rounded text-sm hover:bg-viewer-border"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestAuth}
                disabled={isVerifying || !authReason.trim()}
                className="flex-1 px-4 py-2 bg-detection-child text-white rounded text-sm disabled:opacity-50"
              >
                {isVerifying ? 'Processing...' : 'Request Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}
